<!-- Connection Status Banner Component -->
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { useConnection } from '@/composables/useConnection'
import Button from '@/components/ui/Button.vue'
import ConnectionStatusIcon from './ConnectionStatusIcon.vue'

const { status, dismiss, retry, checkConnection, startPolling, stopPolling } = useConnection()

const bannerHeight = ref(0)

const emit = defineEmits<{
  'height-change': [height: number]
}>()

const bannerClasses = computed(() => [
  'connection-banner',
  `connection-banner--${status.value.state}`,
  { 'connection-banner--dismissed': status.value.isDismissed },
].filter(Boolean).join(' '))

const stateConfig = computed(() => ({
  connected: {
    label: 'Conectado',
    color: 'var(--color-success)',
    bgColor: 'var(--color-success-light)',
  },
  disconnected: {
    label: 'Desconectado',
    color: 'var(--color-warning)',
    bgColor: 'var(--color-warning-light)',
  },
  error: {
    label: 'Error',
    color: 'var(--color-error)',
    bgColor: 'var(--color-error-light)',
  },
  checking: {
    label: 'Verificando...',
    color: 'var(--color-info)',
    bgColor: 'var(--color-info-light)',
  },
}))

const config = computed(() => stateConfig.value[status.value.state] || stateConfig.value.checking)

function handleDismiss() {
  dismiss()
}

function handleRetry() {
  retry()
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)

  if (diffSecs < 60) return 'hace unos segundos'
  if (diffMins < 60) return `hace ${diffMins} min`
  if (diffHours < 24) return `hace ${diffHours} h`
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function updateHeight() {
  nextTick(() => {
    const el = document.querySelector('.connection-banner:not(.connection-banner--dismissed)')
    if (el) {
      const height = el.getBoundingClientRect().height
      bannerHeight.value = height
      emit('height-change', height)
    } else {
      bannerHeight.value = 0
      emit('height-change', 0)
    }
  })
}

watch(() => status.value.state, updateHeight)
watch(() => status.value.isDismissed, updateHeight)

onMounted(() => {
  updateHeight()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<template>
  <div
    v-if="!status.isDismissed || status.state !== 'connected'"
    :class="bannerClasses"
    role="status"
    :aria-live="status.state === 'error' ? 'assertive' : 'polite'"
    :aria-label="`Estado de conexión: ${config.label}`"
  >
    <div class="connection-banner__content">
      <ConnectionStatusIcon :state="status.state" />
     
      <div class="connection-banner__text">
        <span class="connection-banner__label">{{ config.label }}</span>
        <span v-if="status.state === 'error' && status.errorMessage" class="connection-banner__message">
          {{ status.errorMessage }}
        </span>
        <span v-else-if="status.state === 'connected' && status.lastSuccess" class="connection-banner__message">
          Última sincronización: {{ formatTime(status.lastSuccess) }}
        </span>
      </div>

      <div class="connection-banner__actions">
        <Button
          v-if="status.state !== 'connected'"
          variant="ghost"
          size="sm"
          @click="handleRetry"
          :loading="status.state === 'checking'"
        >
          Reintentar
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" class="connection-banner__retry-icon">
            <path d="M23 4v6h-6" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </Button>
       
        <button
          type="button"
          class="connection-banner__dismiss"
          @click="handleDismiss"
          aria-label="Ocultar banner de conexión"
          title="Ocultar"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.connection-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  transform: translateY(0);
  transition: transform var(--transition-normal), opacity var(--transition-normal), height var(--transition-normal), padding var(--transition-normal);
  background: var(--color-bg-card);
  border-bottom: 1px solid var(--color-border-default);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.connection-banner--dismissed {
  transform: translateY(-100%);
  opacity: 0;
  height: 0;
  padding: 0;
  border: none;
  box-shadow: none;
  pointer-events: none;
}

/* State variants */
.connection-banner--connected {
  border-bottom-color: var(--color-success);
}

.connection-banner--disconnected {
  border-bottom-color: var(--color-warning);
}

.connection-banner--error {
  border-bottom-color: var(--color-error);
}

.connection-banner--checking {
  border-bottom-color: var(--color-info);
}

.connection-banner__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-sm) var(--space-lg);
  max-width: var(--container-max);
  margin: 0 auto;
  min-height: 40px;
}

.connection-banner__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.connection-banner__label {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  line-height: 1.3;
}

.connection-banner__message {
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: 1.3;
}

.connection-banner__actions {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  flex-shrink: 0;
}

.connection-banner__retry-icon {
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.connection-banner__dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.connection-banner__dismiss:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.connection-banner__dismiss:focus-visible {
  outline: 2px solid var(--color-brand-primary);
  outline-offset: 2px;
}

.connection-banner__dismiss svg {
  width: 16px;
  height: 16px;
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .connection-banner__content {
    padding: var(--space-sm) var(--space-md);
    gap: var(--space-sm);
  }
 
  .connection-banner__label {
    font-size: var(--text-xs);
  }
 
  .connection-banner__message {
    font-size: var(--text-xs);
  }
}

/* Ensure page content is not hidden behind banner when not dismissed */
.connection-banner:not(.connection-banner--dismissed) + * {
  margin-top: 40px;
}

@media (max-width: 480px) {
  .connection-banner:not(.connection-banner--dismissed) + * {
    margin-top: 44px;
  }
}
</style>